import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { Loader2, RefreshCcw, Smartphone } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useOrgDateFormat } from '@/lib/timezone';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCalendarDate } from '@/lib/utils/date';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  type BiolockrDelivery,
  type BiolockrItemKind,
  type BiolockrSendableItem,
  useBiolockrAvailable,
  useBiolockrDeliveries,
  useBiolockrOptions,
  useCancelBiolockrDelivery,
  useRefreshBiolockrDelivery,
  useSendToBiolockr,
  useWithdrawBiolockrItem,
} from '../api/biolockr-api';

export const NOT_MATCHED_HELP = 'Ask the patient to install biolockr and add this email in Settings, then send again.';
export const PATIENT_REQUESTED_LABEL = 'The patient asked for these records to be sent to this email';

type Variant = 'destructive' | 'info' | 'secondary' | 'success' | 'warning';

const STATUS: Record<BiolockrDelivery['status'], { label: string; variant: Variant }> = {
  sent: { label: 'Sent', variant: 'info' },
  sending: { variant: 'info', label: 'Sending' },
  failed: { label: 'Failed', variant: 'destructive' },
  queued: { variant: 'info', label: 'Awaiting send' },
  delivered: { label: 'Delivered', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
  withdrawn: { label: 'Withdrawn', variant: 'secondary' },
  not_matched: { variant: 'warning', label: 'Not matched' },
  awaiting_upload: { variant: 'info', label: 'Awaiting upload' },
};

const KIND_LABEL: Record<string, string> = {
  report: 'Report',
  imaging: 'Imaging',
  document: 'Document',
  lab_result: 'Lab result',
  prescription: 'Prescription',
};

/** The chip for a delivery. A delivered record the patient has opened reads "Opened". */
export function deliveryStatus(delivery: BiolockrDelivery): { label: string; variant: Variant } {
  if (delivery.status === 'delivered' && delivery.items.some((i) => i.opened_at)) {
    return { label: 'Opened', variant: 'success' };
  }
  return STATUS[delivery.status] ?? { variant: 'secondary', label: delivery.status };
}

function errorMessage(error: unknown): string {
  const e = error as { response?: { data?: { detail?: unknown } } };
  return typeof e.response?.data?.detail === 'string'
    ? e.response.data.detail
    : 'The request failed. Try again in a moment.';
}

function itemStatus(item: BiolockrDelivery['items'][number]): string | null {
  if (item.status === 'withdrawn') return 'Withdrawn';
  if (item.opened_at) return 'Opened';
  if (item.status === 'delivered') return 'Delivered';
  return null;
}

const itemKey = (item: { id: string; kind: BiolockrItemKind }) => `${item.kind}:${item.id}`;

interface Props {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  patientId: string;
  /** Pre-ticks one record, e.g. the report being viewed. */
  preselect?: { id: string; kind: BiolockrItemKind } | null;
}

export function DeliveryHistory({ patientId, deliveries }: { deliveries: BiolockrDelivery[]; patientId: string }) {
  const { formatInstant } = useOrgDateFormat();
  const cancel = useCancelBiolockrDelivery(patientId);
  const refresh = useRefreshBiolockrDelivery(patientId);
  const withdraw = useWithdrawBiolockrItem(patientId);
  const busy = cancel.isPending || refresh.isPending || withdraw.isPending;

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (deliveries.length === 0) return null;
  return (
    <div className="space-y-2 border-t pt-3">
      <Label className="text-sm font-medium">Sent to biolockr</Label>
      <ul className="space-y-3">
        {deliveries.map((delivery) => {
          const chip = deliveryStatus(delivery);
          // A failed delivery may still have been committed at biolockr, so it
          // stays refreshable and withdrawable while biolockr knows it.
          const tracked =
            ['delivered', 'sent', 'withdrawn'].includes(delivery.status) ||
            (delivery.status === 'failed' && delivery.has_remote_delivery);
          return (
            <li key={delivery.id} className="space-y-1 rounded border p-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  {formatInstant(delivery.created_at, "MMM d, yyyy 'at' h:mm a")}
                </span>
                <div className="flex items-center gap-1">
                  {tracked && (
                    <Button
                      size="sm"
                      disabled={busy}
                      variant="ghost"
                      aria-label="Refresh status"
                      onClick={() => run(() => refresh.mutateAsync(delivery.id))}>
                      <RefreshCcw className="h-3 w-3" />
                    </Button>
                  )}
                  {['awaiting_upload', 'queued', 'sent'].includes(delivery.status) && (
                    <Button
                      size="sm"
                      disabled={busy}
                      variant="ghost"
                      onClick={() => {
                        if (window.confirm('Cancel this delivery?')) run(() => cancel.mutateAsync(delivery.id));
                      }}>
                      Cancel
                    </Button>
                  )}
                  <Badge variant={chip.variant}>{chip.label}</Badge>
                </div>
              </div>
              {delivery.status === 'not_matched' && <p className="text-xs">{NOT_MATCHED_HELP}</p>}
              {delivery.status === 'failed' && (
                <p className="text-xs text-muted-foreground">
                  Sending failed{delivery.error_code ? ` (${delivery.error_code})` : ''}.{' '}
                  {delivery.has_remote_delivery
                    ? 'Refresh to check whether biolockr received it before sending again.'
                    : 'Send again to retry.'}
                </p>
              )}
              <ul className="space-y-1">
                {delivery.items.map((item) => {
                  const label = itemStatus(item);
                  return (
                    <li key={item.id} className="flex items-center justify-between gap-2 text-xs">
                      <span className="min-w-0 truncate">{item.title}</span>
                      <span className="flex shrink-0 items-center gap-1">
                        {label && <span className="text-muted-foreground">{label}</span>}
                        {tracked && item.status !== 'withdrawn' && (
                          <Button
                            size="sm"
                            disabled={busy}
                            variant="ghost"
                            className="h-6 px-2 text-xs text-destructive"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Withdraw this record as entered in error? The patient's app will show it as withdrawn by the sender.",
                                )
                              )
                                run(() => withdraw.mutateAsync({ itemId: item.id, deliveryId: delivery.id }));
                            }}>
                            Withdraw
                          </Button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The "Send to biolockr" trigger. Renders nothing unless the person may send
 * and the server has the feature switched on.
 */
export function SendToBiolockrButton({ onClick }: { onClick: () => void }) {
  const available = useBiolockrAvailable();
  if (!available) return null;
  return (
    <Button size="sm" variant="outline" onClick={onClick}>
      <Smartphone className="mr-2 h-4 w-4" /> Send to biolockr
    </Button>
  );
}

export function SendToBiolockrDialog({ open, patientId, preselect, onOpenChange }: Props) {
  const options = useBiolockrOptions(patientId, open);
  const deliveries = useBiolockrDeliveries(patientId, open);
  const send = useSendToBiolockr(patientId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preselectKey = preselect ? itemKey(preselect) : null;

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(preselectKey ? [preselectKey] : []));
    setConfirmed(false);
    setError(null);
  }, [open, preselectKey]);

  const data = options.data;
  const email = data?.recipient_email ?? null;
  const blocker = !data
    ? null
    : !data.has_date_of_birth
      ? "Add the patient's date of birth to the chart before sending to biolockr."
      : !email
        ? "Add the patient's email to the chart before sending to biolockr."
        : null;
  const chosen = (data?.items ?? []).filter((i) => selected.has(itemKey(i)));
  const canSend = !!data && !blocker && chosen.length > 0 && confirmed && !send.isPending;

  const toggle = (item: BiolockrSendableItem) => (checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(itemKey(item));
    else next.delete(itemKey(item));
    setSelected(next);
  };

  const onSend = async () => {
    setError(null);
    try {
      await send.mutateAsync({
        recipient_email: email!,
        patient_requested: true,
        items: chosen.map(({ id, kind }) => ({ id, kind })),
      });
      toast.success('Queued for biolockr');
      setSelected(new Set());
      setConfirmed(false);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send to biolockr</DialogTitle>
          <DialogDescription>
            Send signed reports, documents and imaging into the patient&apos;s own biolockr app. Only signed versions
            are sent; a later amendment needs a new send.
          </DialogDescription>
        </DialogHeader>

        {options.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">What to send</Label>
              <div className="mt-2 space-y-2">
                {(data?.items ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Nothing on this chart can be sent yet.</p>
                )}
                {(data?.items ?? []).map((item) => (
                  <label key={itemKey(item)} className="flex items-start gap-2 text-sm">
                    <Checkbox
                      disabled={!!blocker}
                      aria-label={item.title}
                      checked={selected.has(itemKey(item))}
                      onCheckedChange={(v) => toggle(item)(Boolean(v))}
                    />
                    <span className="min-w-0">
                      <span className="block truncate">{item.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {KIND_LABEL[item.remote_kind] ?? 'Document'}
                        {item.date ? ` · ${formatCalendarDate(item.date)}` : ''}
                        {item.kind !== 'uploaded_document' ? ` · version ${item.version}` : ''}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {blocker ? (
              <p role="alert" className="rounded border border-warning-border bg-warning-surface p-2 text-sm">
                {blocker}
              </p>
            ) : (
              data && (
                <div className="space-y-2">
                  <div>
                    <Label className="text-sm font-medium">Send to</Label>
                    <div className="mt-1 rounded bg-muted px-2 py-1 font-mono text-sm break-all">{email}</div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      The email on the chart. biolockr delivers only if it matches the patient&apos;s account and
                      date of birth.
                    </p>
                  </div>
                  <label className="flex items-start gap-2 text-sm">
                    <Checkbox
                      checked={confirmed}
                      aria-label={PATIENT_REQUESTED_LABEL}
                      onCheckedChange={(v) => setConfirmed(Boolean(v))}
                    />
                    <span>{PATIENT_REQUESTED_LABEL}</span>
                  </label>
                </div>
              )
            )}

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <DialogFooter>
              <Button disabled={!canSend} onClick={onSend}>
                {send.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send
              </Button>
            </DialogFooter>

            <DeliveryHistory patientId={patientId} deliveries={deliveries.data ?? []} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
